import { useCallback } from 'react';

import { WorkflowSurface } from '@/components/surface-host/workflow-surface';
import { ArtifactGallerySurface } from '@/components/surface-host/artifact-gallery-surface';
import { Button } from '@/components/ui/button';
import { ROUTES } from '@/constants/routes';
import { workflowRunPath } from '@/constants/workflow-editor';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { ArtifactViewer } from '@/components/artifacts/artifact-viewer';
import type { ResourceScope } from '@/components/scope-tabs';
import type { ArtifactShareRole } from '@/types/share-types';
import type { ArtifactListItem, ArtifactSortKey } from '@/types/artifacts';

/**
 * The one surface currently occupying the workspace slot. Mirrors the
 * `SurfaceContext` union the agent is grounded with, minus the fields that
 * only matter to the backend.
 *
 * Note: the `workflow` kind here is the frontend-internal descriptor. The
 * backend wire value remains `'workflow_canvas'` and must not change.
 */
export type SurfaceDescriptor =
  | {
      kind: 'artifact';
      artifactId: string;
      /**
       * The artifact detail endpoint does not return user_role, so whoever
       * opened the surface has to carry it. Leaving it out means the viewer's
       * access is unknown, and the destructive actions stay hidden.
       */
      userRole?: ArtifactShareRole;
    }
  | {
      kind: 'workflow';
      workflowId: string;
      /**
       * A run of the workflow to open on the canvas, when the surface was
       * reached from one — a run card in the chat, an artifact's source run.
       * The pane re-attaches to a live run and loads a finished one for
       * review, the same as `/workflow/:id?runId=` does standalone.
       */
      runId?: string;
    }
  | {
      /** Artifacts generated by a specific workflow, shown in the surface pane. */
      kind: 'artifact_gallery';
      workflowId?: string;
      /**
       * Hides the workflow filter and keeps the pane on workflowId. Explicit
       * rather than derived from workflowId's presence, so a pane opened as a
       * browser can filter itself to one workflow without that filter
       * locking the pane and making itself unclearable.
       */
      locked?: boolean;
      /**
       * Which My/Shared tab the pane is on. Part of the descriptor so the
       * host can keep it in the URL: the pane's own state would not survive
       * the reload that everything else about the workspace does.
       */
      scope?: ResourceScope;
      /** Sort order selected in the gallery pane. */
      sort?: ArtifactSortKey;
    };

export type ArtifactGallerySurfaceDescriptor = Extract<
  SurfaceDescriptor,
  { kind: 'artifact_gallery' }
>;

interface SurfaceHostProps {
  surface: SurfaceDescriptor;
  /**
   * Lets a surface hand a workflow back to the host rather than leaving for
   * the standalone editor. Omitted when the host has nowhere to put one.
   */
  onViewWorkflow?: (workflowId: string, runId?: string) => void;
  /**
   * Told when the workflow surface moves to another run, or off one, so the
   * host can keep the descriptor -- and its URL -- naming what the pane shows.
   */
  onRunChange?: (workflowId: string, runId: string | null) => void;
  /**
   * Called when the workflow surface's "View Artifacts" button is clicked.
   * Opens the gallery in the host surface slot. Falls back to /artifacts
   * navigation when omitted.
   */
  onViewArtifacts?: (workflowId: string) => void;
  /**
   * Called when a card inside the artifact gallery pane is clicked. When
   * supplied the artifact opens as a layered surface (in-pane) rather than
   * navigating to the standalone detail route. The callback receives the full
   * list item so the host can carry `user_role`, which the detail endpoint
   * does not return.
   */
  onOpenGalleryArtifact?: (artifact: ArtifactListItem) => void;
  /** Told when the gallery pane changes a filter persisted by its host. */
  onGalleryChange?: (surface: ArtifactGallerySurfaceDescriptor) => void;
  /**
   * Called when the surface's own close affordance is activated. Omit for
   * workflow surfaces when the chat rail is hidden: the close means "go
   * full-page chat", which is meaningless with no chat visible.
   */
  onClose?: () => void;
  /**
   * Called after the workflow surface's workflow is deleted. Omit to fall back
   * to the close affordance, then to the composer list.
   */
  onWorkflowDeleted?: () => void;
  /**
   * Re-enables the workflow surface's unsaved-changes navigation blocker.
   * Only meaningful for a `workflow` surface that is the host route's own
   * anchor rather than a layered peek at another workflow.
   */
  warnOnUnsavedChanges?: boolean;
}

/**
 * Single-slot surface workspace: one surface at a time beside the chat rail,
 * picked by descriptor. Opening a surface replaces whatever was there, so
 * there is no tab collection or per-surface state to retain.
 */
export function SurfaceHost({
  surface,
  onViewWorkflow,
  onRunChange,
  onViewArtifacts,
  onOpenGalleryArtifact,
  onGalleryChange,
  onClose,
  onWorkflowDeleted,
  warnOnUnsavedChanges,
}: SurfaceHostProps) {
  const navigate = useOrgNavigate();

  const handleViewWorkflow = useCallback(
    (workflowId: string, runId?: string) => {
      if (onViewWorkflow) {
        onViewWorkflow(workflowId, runId);
      } else {
        navigate(workflowRunPath(workflowId, runId));
      }
    },
    [onViewWorkflow, navigate],
  );

  // The artifact viewer always needs somewhere to go when it closes or the
  // artifact it was showing is deleted, so unlike the workflow surface it
  // cannot simply drop the affordance. When the host offers no close, fall
  // back to the gallery — the same host-asks-first idiom as onViewWorkflow.
  const handleCloseArtifact = useCallback(() => {
    if (onClose) {
      onClose();
    } else {
      navigate(ROUTES.ARTIFACTS);
    }
  }, [onClose, navigate]);

  // A deleted workflow leaves the pane with nothing to show, so unlike close
  // — which a host may withhold when there is nowhere to go — this always
  // resolves to something: the same host-asks-first idiom as onViewWorkflow.
  const handleWorkflowDeleted = useCallback(() => {
    if (onWorkflowDeleted) {
      onWorkflowDeleted();
    } else if (onClose) {
      onClose();
    } else {
      navigate(ROUTES.WORKFLOW.ROOT);
    }
  }, [onWorkflowDeleted, onClose, navigate]);

  if (surface.kind === 'workflow') {
    const { workflowId } = surface;
    return (
      <WorkflowSurface
        workflowId={workflowId}
        runId={surface.runId}
        onRunChange={onRunChange && ((runId) => onRunChange(workflowId, runId))}
        onClose={onClose}
        onDeleted={handleWorkflowDeleted}
        onViewArtifacts={onViewArtifacts}
        warnOnUnsavedChanges={warnOnUnsavedChanges}
      />
    );
  }

  if (surface.kind === 'artifact_gallery') {
    const { workflowId, locked, scope, sort } = surface;
    return (
      <ArtifactGallerySurface
        workflowId={workflowId}
        lockWorkflowFilter={locked}
        scope={scope}
        sort={sort}
        onScopeChange={
          onGalleryChange &&
          ((next) => onGalleryChange({ ...surface, scope: next }))
        }
        onSortChange={
          onGalleryChange &&
          ((next) => onGalleryChange({ ...surface, sort: next }))
        }
        onWorkflowFilterChange={
          onGalleryChange &&
          ((next) =>
            onGalleryChange({
              ...surface,
              workflowId: next ?? undefined,
            }))
        }
        onClose={onClose}
        onWorkflowNavigate={(wfId, runId) =>
          handleViewWorkflow(wfId, runId ?? undefined)
        }
        onArtifactOpen={onOpenGalleryArtifact}
      />
    );
  }

  return (
    <ArtifactViewer
      artifactId={surface.artifactId}
      userRole={surface.userRole}
      rootClassName='h-full min-w-0'
      titleMaxWidthClass='max-w-64'
      onViewWorkflow={handleViewWorkflow}
      onAfterDelete={handleCloseArtifact}
      loadErrorAction={
        <Button variant='outline' onClick={handleCloseArtifact}>
          Back to chat
        </Button>
      }
      onClose={handleCloseArtifact}
    />
  );
}
